/* Native collection URLs remain usable without JavaScript. */
class ZsCatalog extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.panel = this.querySelector('[data-filter-panel]');
    this.mobile = matchMedia('(max-width: 989px)');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.resize = () => {
      if (!this.panel) return;
      this.panel.close();
      if (!this.mobile.matches) this.panel.show();
    };
    if (this.panel && typeof this.panel.showModal !== 'function') return;
    this.classList.add('catalog--enhanced');
    this.resize();
    this.mobile.addEventListener('change', this.resize, options);
    this.panel?.addEventListener('close', () => {
      if (this.mobile.matches) this.querySelector('[data-open-filters]')?.focus();
    }, options);
    this.addEventListener('click', event => {
      const open = event.target.closest('[data-open-filters]');
      if (open) { this.panel.showModal(); this.panel.querySelector('[data-close-filters]').focus(); return; }
      if (event.target.closest('[data-close-filters]')) { this.panel.close(); return; }
      if (event.target === this.panel && this.mobile.matches) {
        const box = this.panel.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right) this.panel.close();
      }
      const link = event.target.closest('[data-filter-link], .collection__pagination a');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.navigate(link.href, { focusKey: link.dataset.filterKey, pagination: !!link.closest('.collection__pagination') });
    }, options);
    this.addEventListener('change', event => {
      if (!event.target.closest('[data-catalog-sort]')) return;
      const url = new URL(location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      this.navigate(url, { sort: true });
    }, options);
    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-catalog-sort]')) return;
      event.preventDefault();
      const url = new URL(location.href);
      url.searchParams.set('sort_by', new FormData(event.target).get('sort_by'));
      url.searchParams.delete('page');
      this.navigate(url, { sort: true });
    }, options);
    this.addEventListener('input', event => {
      if (!event.target.matches('[data-filter-search]')) return;
      this.searchStyles(event.target.value);
    }, options);
    window.addEventListener('popstate', () => this.navigate(location.href, { history: false }), options);
  }
  disconnectedCallback() { this.controller?.abort(); this.request?.abort(); }
  searchStyles(term) {
    const group = this.querySelector('[data-filter-group="style"]');
    let matches = 0;
    group?.querySelectorAll('[data-filter-option]').forEach(item => {
      item.hidden = !item.textContent.toLowerCase().includes(term.trim().toLowerCase());
      if (!item.hidden) matches++;
    });
    const empty = group?.querySelector('[data-filter-no-match]');
    if (empty) empty.hidden = matches > 0;
  }
  async navigate(destination, context = {}) {
    const url = new URL(destination, location.origin);
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-catalog-status]').textContent = 'Updating prints…';
    const groups = Array.from(this.querySelectorAll('[data-filter-group]'), group => [group.dataset.filterGroup, group.open, group.querySelector('ul')?.scrollTop]);
    const term = this.querySelector('[data-filter-search]')?.value || '';
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(fetchUrl, { signal: request.signal });
      if (!response.ok) throw new Error('Unable to load collection');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('zs-catalog');
      if (!next) throw new Error('Missing collection');
      for (const selector of ['[data-catalog-filters]', '[data-catalog-results]']) {
        const current = this.querySelector(selector), replacement = next.querySelector(selector);
        if (current && replacement) current.replaceChildren(...replacement.childNodes);
      }
      groups.forEach(([key, open, scroll]) => {
        const group = this.querySelector(`[data-filter-group="${key}"]`);
        if (group) { group.open = open; group.querySelector('ul').scrollTop = scroll; }
      });
      const search = this.querySelector('[data-filter-search]');
      if (search) { search.value = term; this.searchStyles(term); }
      const count = this.querySelector('[data-result-count]').dataset.resultCount;
      this.querySelector('[data-catalog-status]').textContent = `${count} ${count === '1' ? 'print' : 'prints'} found.`;
      const show = this.querySelector('[data-show-results]');
      if (show) show.textContent = `Show ${count} ${count === '1' ? 'print' : 'prints'}`;
      if (context.history !== false) history.pushState({}, '', url);
      if (context.sort) this.querySelector('[name="sort_by"]')?.focus();
      else if (context.focusKey) {
        const target = Array.from(this.querySelectorAll('[data-filter-key]')).find(el => el.dataset.filterKey === context.focusKey);
        (target || this.querySelector('[data-open-filters]'))?.focus({ preventScroll: true });
      }
      if (!context.focusKey && !context.sort) {
        const target = this.panel?.matches(':modal') ? this.panel.querySelector('[data-close-filters]') : this.querySelector('[data-result-count]');
        target?.focus({ preventScroll: true });
      }
      if (context.pagination) this.querySelector('[data-catalog-results]').scrollIntoView({ block: 'start' });
      document.dispatchEvent(new CustomEvent('shopify:section:load'));
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(url.href);
    } finally {
      if (this.request === request) this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('zs-catalog')) customElements.define('zs-catalog', ZsCatalog);
